import React, { forwardRef, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";

export type AlertSeverity = "success" | "error" | "warning" | "info";
export type AlertVariant = "filled" | "outlined" | "soft";

export interface AlertProps {
  /** Alert severity/type */
  severity?: AlertSeverity;
  /** Visual variant */
  variant?: "filled" | "outlined" | "soft";
  /** Alert title */
  title?: string;
  /** Alert content */
  children?: React.ReactNode;
  /** Show close button */
  dismissible?: boolean;
  /** Callback when alert is dismissed */
  onClose?: () => void;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Hide icon */
  hideIcon?: boolean;
  /** Action button/element */
  action?: React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
}

/**
 * Arc-style Alert component
 *
 * Features:
 * - Multiple severity levels (success, error, warning, info)
 * - Multiple variants (filled, outlined, soft)
 * - Dismissible with animation
 * - Custom icons and actions
 * - Smooth transitions
 */
const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      severity = "info",
      variant = "soft",
      title,
      children,
      dismissible = false,
      onClose,
      icon,
      hideIcon = false,
      action,
      className = "",
      size = "md",
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isAnimating, setIsAnimating] = useState(false);

    // Handle dismiss
    const handleDismiss = () => {
      setIsAnimating(true);
      setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, 200);
    };

    if (!isVisible) return null;

    // Size styles
    const sizeStyles = {
      sm: {
        padding: "px-3 py-2",
        icon: "w-4 h-4",
        title: "text-sm",
        content: "text-xs",
        close: "w-4 h-4",
      },
      md: {
        padding: "px-4 py-3",
        icon: "w-5 h-5",
        title: "text-sm",
        content: "text-sm",
        close: "w-4 h-4",
      },
      lg: {
        padding: "px-5 py-4",
        icon: "w-6 h-6",
        title: "text-base",
        content: "text-sm",
        close: "w-5 h-5",
      },
    };

    const currentSize = sizeStyles[size];

    // Severity configurations
    const severityConfig = {
      success: {
        icon: <FaCheckCircle className={currentSize.icon} />,
        filled: "bg-success text-white",
        outlined: "border-2 border-success text-success-dark bg-transparent",
        soft: "bg-success-light text-success-dark border border-success/20",
      },
      error: {
        icon: <FaExclamationCircle className={currentSize.icon} />,
        filled: "bg-error text-white",
        outlined: "border-2 border-error text-error-dark bg-transparent",
        soft: "bg-error-light text-error-dark border border-error/20",
      },
      warning: {
        icon: <FaExclamationTriangle className={currentSize.icon} />,
        filled: "bg-warning-dark text-white",
        outlined: "border-2 border-warning-dark text-warning-dark bg-transparent",
        soft: "bg-warning-light text-warning-dark border border-warning-dark/20",
      },
      info: {
        icon: <FaInfoCircle className={currentSize.icon} />,
        filled: "bg-info text-white",
        outlined: "border-2 border-info text-info-dark bg-transparent",
        soft: "bg-info-light text-info-dark border border-info/20",
      },
    };

    const config = severityConfig[severity];
    const variantStyles = config[variant];

    // Get close button styles based on variant
    const getCloseButtonStyles = () => {
      if (variant === "filled") {
        return "text-white/80 hover:text-white hover:bg-white/20";
      }
      return "text-current opacity-60 hover:opacity-100 hover:bg-black/10";
    };

    return (
      <div
        ref={ref}
        role="alert"
        className={`
          flex items-start gap-3
          ${currentSize.padding}
          ${variantStyles}
          rounded-arc-md
          transition-all duration-200
          ${isAnimating ? "opacity-0 transform -translate-y-2" : "opacity-100"}
          ${className}
        `}
      >
        {/* Icon */}
        {!hideIcon && (
          <div className="flex-shrink-0 mt-0.5">
            {icon || config.icon}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <div className={`font-semibold ${currentSize.title} mb-0.5`}>
              {title}
            </div>
          )}
          {children && (
            <div className={currentSize.content}>
              {children}
            </div>
          )}
        </div>

        {/* Action */}
        {action && (
          <div className="flex-shrink-0">
            {action}
          </div>
        )}

        {/* Close button */}
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className={`
              flex-shrink-0
              p-1
              rounded-full
              transition-all duration-arc-fast
              ${getCloseButtonStyles()}
            `}
            aria-label="Dismiss alert"
          >
            <FaTimes className={currentSize.close} />
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = "Alert";

// Alert with auto-dismiss
export interface AutoDismissAlertProps extends AlertProps {
  /** Auto dismiss after duration (ms) */
  duration?: number;
}

const AutoDismissAlert: React.FC<AutoDismissAlertProps> = ({
  duration = 5000,
  onClose,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  if (!isVisible) return null;

  return <Alert {...props} onClose={onClose} />;
};

// Alert Stack for multiple alerts
export interface AlertStackProps {
  children: React.ReactNode;
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "top-center" | "bottom-center";
  className?: string;
}

const AlertStack: React.FC<AlertStackProps> = ({
  children,
  position = "top-right",
  className = "",
}) => {
  const positionStyles = {
    "top-left": "top-4 left-4",
    "top-right": "top-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "top-center": "top-4 left-1/2 -translate-x-1/2",
    "bottom-center": "bottom-4 left-1/2 -translate-x-1/2",
  };

  return (
    <div
      className={`
        fixed z-50
        ${positionStyles[position]}
        flex flex-col gap-2
        max-w-md w-full
        pointer-events-none
        ${className}
      `}
    >
      {React.Children.map(children, (child) => (
        <div className="pointer-events-auto animate-arc-slide-down">
          {child}
        </div>
      ))}
    </div>
  );
};

export { Alert, AutoDismissAlert, AlertStack };
export default Alert;









